import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axios';
import toast from 'react-hot-toast';
import {
  CalendarIcon,
  BanknotesIcon,
  DocumentTextIcon,
  BuildingLibraryIcon,
  UserIcon,
  BuildingOfficeIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

// ============================================
// Component: AdminPaymentReports
// Purpose: Advanced payment reports and analytics
// Features:
// - Daily collections report (NEW)
// - Collections report by agent
// - Top debtors report
// - Checks report (upcoming, returned)
// - Period selection
// - Export functionality
// ============================================

const AdminPaymentReports = () => {
  const [activeReport, setActiveReport] = useState('daily'); // daily, collections, debts, checks
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    date_from: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0]
  });

  // Daily Collections Report State (NEW)
  const [dailyData, setDailyData] = useState(null);
  const [dailyDateMode, setDailyDateMode] = useState('range'); // 'single' or 'range'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dailyDateFrom, setDailyDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dailyDateTo, setDailyDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [selectedCollector, setSelectedCollector] = useState('');

  // Payment Detail Modal State
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentDetailLoading, setPaymentDetailLoading] = useState(false);
  const [paymentDetail, setPaymentDetail] = useState(null);

  // Collections Report State
  const [collectionsData, setCollectionsData] = useState(null);

  // Debts Report State
  const [debtsData, setDebtsData] = useState(null);

  // Checks Report State
  const [checksData, setChecksData] = useState(null);
  const [checksDaysFilter, setChecksDaysFilter] = useState(30);

  useEffect(() => {
    if (activeReport === 'daily') {
      fetchDailyCollections();
    } else if (activeReport === 'collections') {
      fetchCollectionsReport();
    } else if (activeReport === 'debts') {
      fetchDebtsReport();
    } else if (activeReport === 'checks') {
      fetchChecksReport();
    }
  }, [activeReport, dateRange, checksDaysFilter, selectedDate, dailyDateFrom, dailyDateTo, dailyDateMode, selectedCollector]);

  // NEW: Fetch daily collections
  const fetchDailyCollections = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (dailyDateMode === 'range') {
        params.append('date_from', dailyDateFrom);
        params.append('date_to', dailyDateTo);
      } else {
        params.append('date', selectedDate);
      }
      
      if (selectedCollector) {
        params.append('collector_id', selectedCollector);
      }
      const response = await axiosInstance.get(`/api/admin/payments/daily-collections?${params.toString()}`);
      setDailyData(response.data);
    } catch (error) {
      toast.error('שגיאה בטעינת דוח גבייה יומי');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodLabel = (method) => {
    const methods = { cash: 'מזומן', check: 'צ\'ק', bank_transfer: 'העברה בנקאית' };
    return methods[method] || method;
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'cash': return <BanknotesIcon className="h-4 w-4 text-green-600" />;
      case 'check': return <DocumentTextIcon className="h-4 w-4 text-blue-600" />;
      case 'bank_transfer': return <BuildingLibraryIcon className="h-4 w-4 text-purple-600" />;
      default: return null;
    }
  };

  // Fetch full payment details
  const fetchPaymentDetail = async (paymentId) => {
    try {
      setPaymentDetailLoading(true);
      const response = await axiosInstance.get(`/api/admin/payments/${paymentId}`);
      // API returns { payment: {...}, allocations: [...], summary: {...} }
      setPaymentDetail({
        ...response.data.payment,
        allocations: response.data.allocations,
        summary: response.data.summary
      });
    } catch (error) {
      toast.error('שגיאה בטעינת פרטי תשלום');
    } finally {
      setPaymentDetailLoading(false);
    }
  };

  // Open payment detail modal
  const openPaymentDetail = (payment) => {
    setSelectedPayment(payment);
    fetchPaymentDetail(payment.id);
  };

  // Close payment detail modal
  const closePaymentDetail = () => {
    setSelectedPayment(null);
    setPaymentDetail(null);
  };

  // Get check status label
  const getCheckStatusLabel = (status) => {
    const statuses = {
      pending: 'ממתין',
      deposited: 'הופקד',
      cleared: 'נפרע',
      bounced: 'חזר'
    };
    return statuses[status] || status;
  };

  // Get check status color
  const getCheckStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      deposited: 'bg-blue-100 text-blue-800',
      cleared: 'bg-green-100 text-green-800',
      bounced: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  };

  const exportDailyToCSV = () => {
    if (!dailyData?.collectors) return;
    const rows = [['גובה', 'חברה', 'סכום', 'אמצעי תשלום', 'מס\' קבלה', 'תאריך', 'שעה']];
    dailyData.collectors.forEach(c => {
      c.payments.forEach(p => {
        rows.push([c.collector.name, p.company.name, p.amount, getPaymentMethodLabel(p.payment_method), p.receipt_number, formatDate(p.payment_date), formatTime(p.created_at)]);
      });
    });
    const csvContent = '\uFEFF' + rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const filename = dailyDateMode === 'range' 
      ? `collections-${dailyDateFrom}-to-${dailyDateTo}.csv`
      : `collections-${selectedDate}.csv`;
    link.download = filename;
    link.click();
    toast.success('הקובץ הורד בהצלחה');
  };

  const fetchCollectionsReport = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/admin/payments/reports/collections', {
        params: dateRange
      });
      setCollectionsData(response.data);
    } catch (error) {
      toast.error('שגיאה בטעינת דוח גבייה');
    } finally {
      setLoading(false);
    }
  };

  const fetchDebtsReport = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/admin/payments/reports/debts', {
        params: { sort: 'highest', limit: 20 }
      });
      setDebtsData(response.data);
    } catch (error) {
      toast.error('שגיאה בטעינת דוח חובות');
    } finally {
      setLoading(false);
    }
  };

  const fetchChecksReport = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/admin/payments/reports/checks', {
        params: { days_until_due: checksDaysFilter }
      });
      setChecksData(response.data);
    } catch (error) {
      toast.error('שגיאה בטעינת דוח צ\'קים');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" dir="rtl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">דוחות גבייה</h1>
        <p className="text-gray-600">דוחות מתקדמים וניתוח נתונים</p>
      </div>

      {/* Report Type Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            <button
              onClick={() => setActiveReport('daily')}
              className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeReport === 'daily'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              דוח גבייה יומי
            </button>
            <button
              onClick={() => setActiveReport('collections')}
              className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeReport === 'collections'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              דוח גבייה לפי סוכן
            </button>
            <button
              onClick={() => setActiveReport('debts')}
              className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeReport === 'debts'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              דוח חובות
            </button>
            <button
              onClick={() => setActiveReport('checks')}
              className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeReport === 'checks'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              דוח צ'קים
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Daily Collections Report - NEW */}
          {activeReport === 'daily' && (
            <>
              {/* Filters */}
              <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                {/* Date Mode Toggle */}
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-sm font-medium text-gray-700">בחירת תאריכים:</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setDailyDateMode('single')}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        dailyDateMode === 'single'
                          ? 'bg-primary-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      יום בודד
                    </button>
                    <button
                      onClick={() => setDailyDateMode('range')}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        dailyDateMode === 'range'
                          ? 'bg-primary-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      טווח תאריכים
                    </button>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-end gap-4">
                  {dailyDateMode === 'single' ? (
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1">תאריך</label>
                      <div className="relative">
                        <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="date"
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          className="w-full pr-10 pl-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">מתאריך</label>
                        <div className="relative">
                          <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="date"
                            value={dailyDateFrom}
                            onChange={(e) => setDailyDateFrom(e.target.value)}
                            className="w-full pr-10 pl-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">עד תאריך</label>
                        <div className="relative">
                          <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="date"
                            value={dailyDateTo}
                            onChange={(e) => setDailyDateTo(e.target.value)}
                            className="w-full pr-10 pl-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">גובה</label>
                    <select
                      value={selectedCollector}
                      onChange={(e) => setSelectedCollector(e.target.value)}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">כל הגובים</option>
                      {dailyData?.all_collectors?.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={exportDailyToCSV}
                    disabled={!dailyData?.collectors?.length}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    ייצוא CSV
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : dailyData ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg p-4 text-white col-span-2 md:col-span-1">
                      <div className="flex items-center gap-2 mb-1">
                        <BanknotesIcon className="h-5 w-5" />
                        <span className="text-xs opacity-90">סה"כ גבייה</span>
                      </div>
                      <div className="text-xl font-bold">{formatCurrency(dailyData.summary.grand_total)}</div>
                      <div className="text-xs opacity-75">{dailyData.summary.total_payments} תשלומים</div>
                    </div>
                    <div className="bg-white rounded-lg border p-4">
                      <div className="flex items-center gap-1 mb-1">
                        <BanknotesIcon className="h-4 w-4 text-green-600" />
                        <span className="text-xs text-gray-600">מזומן</span>
                      </div>
                      <div className="text-lg font-bold">{formatCurrency(dailyData.summary.by_method.cash.total)}</div>
                      <div className="text-xs text-gray-500">{dailyData.summary.by_method.cash.count} תשלומים</div>
                    </div>
                    <div className="bg-white rounded-lg border p-4">
                      <div className="flex items-center gap-1 mb-1">
                        <DocumentTextIcon className="h-4 w-4 text-blue-600" />
                        <span className="text-xs text-gray-600">צ'קים</span>
                      </div>
                      <div className="text-lg font-bold">{formatCurrency(dailyData.summary.by_method.check.total)}</div>
                      <div className="text-xs text-gray-500">{dailyData.summary.by_method.check.count} תשלומים</div>
                    </div>
                    <div className="bg-white rounded-lg border p-4">
                      <div className="flex items-center gap-1 mb-1">
                        <BuildingLibraryIcon className="h-4 w-4 text-purple-600" />
                        <span className="text-xs text-gray-600">העברות</span>
                      </div>
                      <div className="text-lg font-bold">{formatCurrency(dailyData.summary.by_method.bank_transfer.total)}</div>
                      <div className="text-xs text-gray-500">{dailyData.summary.by_method.bank_transfer.count} תשלומים</div>
                    </div>
                    <div className="bg-white rounded-lg border p-4">
                      <div className="flex items-center gap-1 mb-1">
                        <UserIcon className="h-4 w-4 text-orange-600" />
                        <span className="text-xs text-gray-600">גובים</span>
                      </div>
                      <div className="text-lg font-bold">{dailyData.summary.total_collectors}</div>
                      <div className="text-xs text-gray-500">פעילים בתקופה</div>
                    </div>
                  </div>

                  {/* Date Range Info */}
                  {dailyDateMode === 'range' && (
                    <div className="mb-4 text-sm text-gray-600 flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      <span>תקופה: {formatDate(dailyDateFrom)} - {formatDate(dailyDateTo)}</span>
                    </div>
                  )}

                  {/* Collectors Breakdown */}
                  {dailyData.collectors?.length > 0 ? (
                    <div className="space-y-4">
                      {dailyData.collectors.map((collector) => (
                        <div key={collector.collector.id} className="border rounded-lg overflow-hidden">
                          {/* Collector Header */}
                          <div className="bg-gray-50 px-4 py-3 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center">
                                <UserIcon className="h-4 w-4 text-primary-600" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">{collector.collector.name}</h3>
                                {collector.collector.region && (
                                  <span className="text-xs text-gray-500">אזור: {collector.collector.region}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-gray-500">{collector.payments_count} תשלומים</span>
                              <span className="text-lg font-bold text-green-600">{formatCurrency(collector.total_collected)}</span>
                            </div>
                          </div>

                          {/* Payments Table */}
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">חברה</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">סכום</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">אמצעי</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">קבלה</th>
                                  {dailyDateMode === 'range' && (
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">תאריך</th>
                                  )}
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 hidden sm:table-cell">שעה</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {collector.payments.map((payment) => (
                                  <tr 
                                    key={payment.id} 
                                    className="hover:bg-primary-50 cursor-pointer transition-colors"
                                    onClick={() => openPaymentDetail(payment)}
                                  >
                                    <td className="px-4 py-2">
                                      <div className="flex items-center gap-1">
                                        <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                                        <span className="text-sm font-medium">{payment.company.name}</span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm font-bold text-green-600">
                                      {formatCurrency(payment.amount)}
                                    </td>
                                    <td className="px-4 py-2">
                                      <div className="flex items-center gap-1">
                                        {getPaymentMethodIcon(payment.payment_method)}
                                        <span className="text-sm">{getPaymentMethodLabel(payment.payment_method)}</span>
                                        {payment.payment_method === 'check' && payment.checks_count > 0 && (
                                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                            {payment.checks_count} צ'קים
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600 font-mono">
                                      {payment.receipt_number}
                                    </td>
                                    {dailyDateMode === 'range' && (
                                      <td className="px-4 py-2 text-sm text-gray-600">
                                        {formatDate(payment.payment_date)}
                                      </td>
                                    )}
                                    <td className="px-4 py-2 text-sm text-gray-500 hidden sm:table-cell">
                                      {formatTime(payment.created_at)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <BanknotesIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>אין גביות בתאריך זה</p>
                    </div>
                  )}
                </>
              ) : null}
            </>
          )}

          {/* Collections Report */}
          {activeReport === 'collections' && (
            <>
              {/* Date Range Filter */}
              <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">בחירת תקופה</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">מתאריך</label>
                    <input
                      type="date"
                      value={dateRange.date_from}
                      onChange={(e) => setDateRange({ ...dateRange, date_from: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">עד תאריך</label>
                    <input
                      type="date"
                      value={dateRange.date_to}
                      onChange={(e) => setDateRange({ ...dateRange, date_to: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={fetchCollectionsReport}
                      className="w-full bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 text-sm font-medium"
                    >
                      עדכן דוח
                    </button>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : collectionsData ? (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 mb-1">סה"כ נגבה</div>
                      <div className="text-2xl font-bold text-blue-600">
                        {formatCurrency(collectionsData.total || 0)}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 mb-1">מספר סוכנים</div>
                      <div className="text-2xl font-bold text-green-600">
                        {collectionsData.collections?.length || 0}
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 mb-1">תקופה</div>
                      <div className="text-sm font-semibold text-purple-600">
                        {formatDate(dateRange.date_from)} - {formatDate(dateRange.date_to)}
                      </div>
                    </div>
                  </div>

                  {/* Collections Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">סוכן</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">סה"כ נגבה</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">מספר תשלומים</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">מזומן</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">צ'ק</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">העברה</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {collectionsData.collections?.map((collection, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {collection.agent?.name || 'לא ידוע'}
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-green-600">
                              {formatCurrency(collection.total_collected)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {collection.count}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {formatCurrency(collection.by_method?.cash || 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {formatCurrency(collection.by_method?.check || 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {formatCurrency(collection.by_method?.bank_transfer || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>לא נמצאו נתונים לתקופה זו</p>
                </div>
              )}
            </>
          )}

          {/* Debts Report */}
          {activeReport === 'debts' && (
            <>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : debtsData ? (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 mb-1">סה"כ חובות</div>
                      <div className="text-2xl font-bold text-red-600">
                        {formatCurrency(debtsData.summary?.total_debt || 0)}
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 mb-1">חוב התחלתי (כולל)</div>
                      <div className="text-2xl font-bold text-blue-600">
                        {formatCurrency(debtsData.summary?.total_opening_balance || 0)}
                      </div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 mb-1">חריגות מסגרת</div>
                      <div className="text-2xl font-bold text-yellow-600">
                        {debtsData.summary?.over_limit_count || 0}
                      </div>
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-4">TOP 20 חברות עם חוב</h3>

                  {/* Debts Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">#</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">חברה</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">ח.פ</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">מסגרת</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">חוב התחלתי</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">הזמנות</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">תשלומים</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">סה"כ חוב</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">סטטוס</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {debtsData.debts?.map((debt, index) => (
                          <tr key={debt.company_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {debt.company_name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {debt.business_number}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {formatCurrency(debt.credit_limit)}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-600">
                              {debt.opening_balance > 0 ? formatCurrency(debt.opening_balance) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {formatCurrency(debt.orders_total || 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-green-600">
                              -{formatCurrency(debt.payments_total || 0)}
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-red-600">
                              {formatCurrency(debt.current_balance)}
                            </td>
                            <td className="px-4 py-3">
                              {debt.over_limit ? (
                                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 font-bold">
                                  חריגת מסגרת
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">
                                  חוב תקין
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Explanation */}
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
                    <strong>נוסחת חישוב:</strong> סה"כ חוב = חוב התחלתי + הזמנות - תשלומים
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>לא נמצאו נתונים</p>
                </div>
              )}
            </>
          )}

          {/* Checks Report */}
          {activeReport === 'checks' && (
            <>
              {/* Days Filter */}
              <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">תצוגת צ'קים</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setChecksDaysFilter(7)}
                    className={`px-4 py-2 text-sm rounded-md ${checksDaysFilter === 7
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    7 ימים
                  </button>
                  <button
                    onClick={() => setChecksDaysFilter(14)}
                    className={`px-4 py-2 text-sm rounded-md ${checksDaysFilter === 14
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    14 ימים
                  </button>
                  <button
                    onClick={() => setChecksDaysFilter(30)}
                    className={`px-4 py-2 text-sm rounded-md ${checksDaysFilter === 30
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    30 ימים
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : checksData ? (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 mb-1">ממתינים</div>
                      <div className="text-2xl font-bold text-yellow-600">
                        {checksData.summary?.by_status?.pending || 0}
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 mb-1">הופקדו</div>
                      <div className="text-2xl font-bold text-blue-600">
                        {checksData.summary?.by_status?.deposited || 0}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 mb-1">פרועים</div>
                      <div className="text-2xl font-bold text-green-600">
                        {checksData.summary?.by_status?.cleared || 0}
                      </div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 mb-1">חזרו</div>
                      <div className="text-2xl font-bold text-red-600">
                        {checksData.summary?.by_status?.returned || 0}
                      </div>
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    צ'קים עתידיים ({checksDaysFilter} ימים)
                  </h3>

                  {/* Checks Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">מס' צ'ק</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">חברה</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">תאריך</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">ימים עד פירעון</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">סכום</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">הערות</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {checksData.checks?.pending?.map((check) => (
                          <tr key={check.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {check.check_number}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {check.company_name || 'לא ידוע'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {formatDate(check.check_date)}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-sm font-medium ${check.days_until_due <= 7 ? 'text-red-600' :
                                  check.days_until_due <= 14 ? 'text-yellow-600' :
                                    'text-green-600'
                                }`}>
                                {check.days_until_due} ימים
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-green-600">
                              {formatCurrency(check.amount)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {check.notes || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {(!checksData.checks?.pending || checksData.checks.pending.length === 0) && (
                    <div className="text-center py-12 text-gray-500">
                      <p>אין צ'קים ממתינים לתקופה זו</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>לא נמצאו נתונים</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Payment Detail Modal */}
      {selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" dir="rtl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <BanknotesIcon className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">פרטי תשלום</h2>
                  <p className="text-sm text-gray-500">קבלה #{selectedPayment.receipt_number}</p>
                </div>
              </div>
              <button
                onClick={closePaymentDetail}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {paymentDetailLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-3 text-gray-500">טוען פרטים...</p>
                </div>
              ) : paymentDetail ? (
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-xs text-gray-500 mb-1">סכום</div>
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(paymentDetail.amount)}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-xs text-gray-500 mb-1">אמצעי תשלום</div>
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        {getPaymentMethodIcon(paymentDetail.payment_method)}
                        {getPaymentMethodLabel(paymentDetail.payment_method)}
                      </div>
                    </div>
                  </div>

                  {/* Company & Collector Info */}
                  <div className="bg-white border rounded-lg divide-y">
                    <div className="p-4 flex items-start gap-3">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-xs text-gray-500">חברה</div>
                        <div className="font-semibold">{paymentDetail.company?.name || 'לא זמין'}</div>
                        {paymentDetail.company?.business_number && (
                          <div className="text-sm text-gray-500">ח.פ: {paymentDetail.company.business_number}</div>
                        )}
                      </div>
                    </div>
                    <div className="p-4 flex items-start gap-3">
                      <UserIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-xs text-gray-500">גובה</div>
                        <div className="font-semibold">
                          {paymentDetail.collector ? 
                            `${paymentDetail.collector.first_name} ${paymentDetail.collector.last_name}` : 
                            'לא זמין'}
                        </div>
                        {paymentDetail.collector?.email && (
                          <div className="text-sm text-gray-500">{paymentDetail.collector.email}</div>
                        )}
                      </div>
                    </div>
                    <div className="p-4 flex items-start gap-3">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-xs text-gray-500">תאריך תשלום</div>
                        <div className="font-semibold">{formatDate(paymentDetail.payment_date)}</div>
                        <div className="text-sm text-gray-500">
                          נוצר: {formatDate(paymentDetail.created_at)} {formatTime(paymentDetail.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Check Details Section */}
                  {paymentDetail.payment_method === 'check' && paymentDetail.checks && paymentDetail.checks.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-blue-50 px-4 py-3 border-b flex items-center gap-2">
                        <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                        <span className="font-semibold text-blue-800">פרטי צ'קים ({paymentDetail.checks.length})</span>
                      </div>
                      <div className="divide-y">
                        {paymentDetail.checks.map((check, idx) => (
                          <div key={check.id || idx} className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                                  #{check.check_number}
                                </span>
                                <span className={`text-xs px-2 py-1 rounded-full ${getCheckStatusColor(check.status)}`}>
                                  {getCheckStatusLabel(check.status)}
                                </span>
                              </div>
                              <span className="font-bold text-green-600">{formatCurrency(check.amount)}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-gray-500">תאריך פירעון: </span>
                                <span className="font-medium">{formatDate(check.check_date)}</span>
                              </div>
                              {check.bank_name && (
                                <div>
                                  <span className="text-gray-500">בנק: </span>
                                  <span className="font-medium">{check.bank_name}</span>
                                </div>
                              )}
                              {check.bank_branch && (
                                <div>
                                  <span className="text-gray-500">סניף: </span>
                                  <span className="font-medium">{check.bank_branch}</span>
                                </div>
                              )}
                              {check.account_number && (
                                <div>
                                  <span className="text-gray-500">חשבון: </span>
                                  <span className="font-medium">{check.account_number}</span>
                                </div>
                              )}
                            </div>
                            {check.notes && (
                              <div className="mt-2 text-sm text-gray-500 bg-gray-50 p-2 rounded">
                                {check.notes}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Bank Transfer Details */}
                  {paymentDetail.payment_method === 'bank_transfer' && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-purple-50 px-4 py-3 border-b flex items-center gap-2">
                        <BuildingLibraryIcon className="h-5 w-5 text-purple-600" />
                        <span className="font-semibold text-purple-800">פרטי העברה בנקאית</span>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {paymentDetail.bank_transfer_reference && (
                            <div>
                              <span className="text-gray-500">מספר אסמכתא: </span>
                              <span className="font-medium font-mono">{paymentDetail.bank_transfer_reference}</span>
                            </div>
                          )}
                          {paymentDetail.bank_transfer_date && (
                            <div>
                              <span className="text-gray-500">תאריך העברה: </span>
                              <span className="font-medium">{formatDate(paymentDetail.bank_transfer_date)}</span>
                            </div>
                          )}
                          {paymentDetail.bank_account_to && (
                            <div className="col-span-2">
                              <span className="text-gray-500">לחשבון: </span>
                              <span className="font-medium">{paymentDetail.bank_account_to}</span>
                            </div>
                          )}
                        </div>
                        {!paymentDetail.bank_transfer_reference && !paymentDetail.bank_transfer_date && (
                          <p className="text-gray-500 text-sm">אין פרטים נוספים על ההעברה</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cash Details */}
                  {paymentDetail.payment_method === 'cash' && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-green-50 px-4 py-3 border-b flex items-center gap-2">
                        <BanknotesIcon className="h-5 w-5 text-green-600" />
                        <span className="font-semibold text-green-800">פרטי מזומן</span>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircleIcon className="h-5 w-5" />
                          <span className="font-medium">תשלום במזומן התקבל</span>
                        </div>
                        {paymentDetail.cash_discount && parseFloat(paymentDetail.cash_discount) > 0 && (
                          <div className="mt-3 p-3 bg-green-50 rounded-lg">
                            <span className="text-sm text-gray-600">הנחת מזומן: </span>
                            <span className="font-bold text-green-600">{formatCurrency(paymentDetail.cash_discount)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes Section */}
                  {paymentDetail.notes && (
                    <div className="border rounded-lg p-4">
                      <div className="text-xs text-gray-500 mb-1">הערות</div>
                      <p className="text-sm">{paymentDetail.notes}</p>
                    </div>
                  )}

                  {/* Related Orders */}
                  {paymentDetail.paymentOrders && paymentDetail.paymentOrders.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-3 border-b">
                        <span className="font-semibold">הזמנות משויכות ({paymentDetail.paymentOrders.length})</span>
                      </div>
                      <div className="divide-y">
                        {paymentDetail.paymentOrders.map((po) => (
                          <div key={po.order?.id || po.id} className="p-4 flex items-center justify-between">
                            <div>
                              <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                                #{po.order?.order_number || po.order?.id || 'N/A'}
                              </span>
                              <span className="mr-3 text-sm text-gray-500">
                                {po.order?.created_at ? formatDate(po.order.created_at) : ''}
                              </span>
                            </div>
                            <span className="font-bold">{formatCurrency(po.amount || po.order?.total_amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>לא ניתן לטעון את פרטי התשלום</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
              <button
                onClick={closePaymentDetail}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentReports;
